export interface IToggleEvent {
  value: string;
  toggle: boolean;
}

export interface INextEvent {
  value: any;
  toggles?: { [name: string]: boolean };
  gameId?: number;
}

export interface IYardageSelectionEvent extends INextEvent {
  yardLine: number;
  side: TTeamSide;
}

export enum TTeam {
  HOME = 'home',
  AWAY = 'away',
}

export enum TTeamSide {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum TOffDef {
  OFFENSE = 'OFFENSE',
  DEFENSE = 'DEFENSE',
  BOTH = 'BOTH',
}

export interface IPlayflowPlay {
  id?: number;
  createdAt?: string;
  driveNumber: number;
  drivePlayNumber: number;
  startingYardLine: number | null;
  startingDown: number;
  startingDistance: number;
  startingIsHomeTeamSideOfField: boolean;
  resultingIsHomeTeamSideOfField: boolean;
  homeTeamStartedWithPossession: boolean;
  homeTeamEndedWithPossession: boolean;
  playResultTypeId: number;
  refEventOutcomeId?: number;
  playDesignId: number;
  isPlayNullified: boolean;
  lateral: boolean;
  qb1Out: boolean;
  playerStats?: IPlayerStat[];
  isEdited?: boolean;
  resultingDown: number;
  resultingDistance: number;
  resultingYardLine: number | null;
  fumbleYardLine: number | null;
  fumbleIsHomeTeamSideOfField: boolean | null;
  penaltyYardLine: number | null;
  penaltyIsHomeTeamSideOfField: boolean | null;
}

export interface IPlayerStat {
  id?: number;
  createdAt?: string;
  playerId: number;
  roleId: number;
  playId?: number;
  statisticalYardage?: number;
  ignoreAttempt: boolean;
  attempt: boolean;
  touchdown: boolean;
  interception: boolean;
  complete: boolean;
}

export interface IPlayFlow {
  plays: IPlayflowPlay[];
  teams: {
    left: TTeam | null;
    right: TTeam | null;
  };
  driveNumber: number;
  drivePlayNumber: number;
  driveDirection: TTeamSide | null;
  yardLine: {
    number: number;
    side: TTeamSide;
  } | null;
  down: number | null;
  distance: number | null;
  activeTeam: TTeam | null;
  currentQuarter: number;
  editing: { drive: number; play: number } | null;
  inserting: { drive: number; play: number; beforeDrive: number; beforePlay: number } | null;
  isQb1Out: { home: boolean; away: boolean };
  startersOut: { home: boolean; away: boolean };
  lastOverTimeScore: TTeam | null;
  hasStartedOvertime: boolean;
  isStaleLastPlay: boolean;
  isEditedQbStatus: boolean;
  isMostRecentPlayPossessionEdited: {
    originalNextDriveNumber: number;
    originalNextDrivePlayNumber: number;
  } | null;
}

export enum TInputTypes {
  GAME_SELECTOR = 'GameSelector',
  PLAYER_PREVIEW = 'PlayerPreview',
  YARDAGE_SELECTOR = 'YardageSelector',
  CONFIRMATION_SELECTOR = 'ConfirmationSelector',
  TWO_ROW_GENERIC = 'GenericSelectorTwoRow',
  NONE = 'NONE',
}

export enum TPlays {
  KICK = 'KICK',
  RECEIVE = 'RECEIVE',
  KICKOFF = 'KICKOFF',
  ONSIDE = 'ONSIDE KICK',
  REF = 'REF EVENT',
  PUNT = 'PUNT',
  FAKE_PUNT = 'FAKE PUNT',
  REGULAR = 'REGULAR',
  PENALTY_ALERT = 'PENALTY ALERT',
  FG = 'FG',
  FAKE_FG = 'FAKE FG',
  RUSH = 'RUSH',
  PASS = 'PASS',
  FUMBLE = 'FUMBLE',
  PAT = 'PAT',
  OFFENSE = 'OFFENSE',
  DEFENCE = 'DEFENSE',
  LATERAL = 'LATERAL',
}

export enum TPlayResults {
  TOUCHBACK = 'TOUCHBACK',
  RETURN = 'KICK RETURN',
  OUT_OF_BOUNDS = 'OUT OF BOUNDS',
  ONSIDE_RECOVER = 'ONSIDE KICK RECOVER',
  TURNOVER = 'TURNOVER',
  SCORE = 'SCORE',
  PENALTY = 'PENALTY',
  NEXT = 'NEXT',
  FUMBLE_LOST = 'FUMBLE LOST',
  FUMBLE_RECOVERED = 'FUMBLE RECOVERED',
  NO_FUMBLE = 'NO FUMBLE',
  INTERCEPTION = 'INTERCEPTION',
  DECLINED = 'DECLINED',
  CONFIRMED = 'CONFIRMED',
  OVERTURNED = 'OVERTURNED',
  SHORT = 'SHORT',
  FIRST_DOWN = 'FIRST DOWN',
  TOUCHDOWN = 'TOUCHDOWN',
  EXTRA_POINT = 'EXTRA POINT',
  TWO_PT_MADE = '2PT MADE',
  TWO_PT_MISS = '2PT MISSED',
  TWO_PT_CONVERSION = '2PT CONVERSION',
  ONE_PT_SAFTEY = '1PT SAFETY',
  TWO_PT_RETURN = '2PT RETURN',
  FG_RETURN = 'FG RETURN',
  FG_MISS = 'FG MISS',
  FG_MAKE = 'FG MAKE',
  FAIR_CATCH_OR_DOWNED = 'FAIR CATCH DOWNED O.B.',
  PUNT_RETURN = 'PUNT RETURN',
  SAFTEY = 'SAFETY',
  CLOSE_1ST = 'CLOSE 1ST',
  PLAYER_ATTRIBUTION = 'PLAYER_ATTRIBUTION',
  GROUNDING = 'GROUNDING',
}

export enum TRefEvents {
  REVIEW = 'UNDER REVIEW',
  PENALTY = 'PENALTY',
  NO_PLAY = 'NO PLAY',
  CHAINS = 'CHAINS',
  CHALLENGE = 'CHALLENGE',
}

export enum TPassPlays {
  QB_SCRAMBLE = 'QB SCRAMBLE',
  INCOMPLETE = 'INCOMPLETE',
  COMPLETE = 'COMPLETE',
  INTERCEPTION = 'INTERCEPTION',
}

export enum TPatEvents {
  XP_ATTEMPT = 'XP ATTEMPT',
  TWO_PT_ATTEMPT = '2PT ATTEMPT',
  TWO_PT_RET_ONE_PT_SAFTEY = '2PT RETURN 1PT SAFETY',
  XP_RETURN = 'XP RETURN',
  XP_MISS = 'XP MISS',
  XP_MADE = 'XP MADE',
}

export enum TAltPlayerOptions {
  OTHER = 'OTHER',
  NON_QB_1 = 'NON-QB1 PASSER',
}

export enum TPenaltyDownResult {
  FIRST_DOWN = 'FIRST DOWN',
  REPLAY_DOWN = 'REPLAY DOWN',
}

export enum TPenaltyDriveResult {
  CONTINUE = 'CONTINUE EXISTING DRIVE',
  NEW = 'BEGIN NEW DRIVE',
}
